import { useEffect, useState } from "react";
import { Headphones, Mic, ShieldCheck } from "lucide-react";
import { getSavedMic, listInputDevices, saveMic, unlockMicLabels } from "@/lib/creatorVocal";

/** "Built-in Microphone ▼" + headphones hint. Labels unlock after one permission grant. */
export default function MicPicker({ deviceId, onChange, disabled }) {
  const [devices, setDevices] = useState([]);
  const [unlocking, setUnlocking] = useState(false);

  const refresh = async () => {
    try {
      const list = await listInputDevices();
      setDevices(list);
      if (deviceId && !list.some((d) => d.deviceId === deviceId)) onChange("");
    } catch (e) { void e; }
  };

  useEffect(() => {
    refresh();
    const md = navigator.mediaDevices;
    md?.addEventListener?.("devicechange", refresh);
    return () => md?.removeEventListener?.("devicechange", refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlabeled = devices.length === 0 || devices.every((d) => d.unlabeled);

  const allow = async () => {
    setUnlocking(true);
    try { await unlockMicLabels(); await refresh(); } catch (e) { void e; } finally { setUnlocking(false); }
  };

  return (
    <div data-testid="mic-picker" className="rounded-3xl border border-white/[0.08] bg-black/25 p-5">
      <p className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/70"><Mic className="h-3.5 w-3.5" /> Microphone</p>
      <select
        data-testid="mic-select"
        value={deviceId || ""}
        disabled={disabled}
        onChange={(e) => { onChange(e.target.value); saveMic(e.target.value); }}
        className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white focus:border-blue-400/60 focus:outline-none disabled:opacity-50"
      >
        <option value="">{devices[0]?.label && !devices[0].unlabeled ? `Default — ${devices[0].label}` : "Built-in Microphone (default)"}</option>
        {devices.filter((d) => d.deviceId && d.deviceId !== "default").map((d) => (
          <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
        ))}
      </select>
      {unlabeled ? (
        <button type="button" data-testid="mic-allow-btn" onClick={allow} disabled={unlocking || disabled} className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-blue-200 hover:text-white disabled:opacity-50">
          <ShieldCheck className="h-3.5 w-3.5" /> {unlocking ? "Asking…" : "Allow microphone to see all inputs"}
        </button>
      ) : null}
      <p data-testid="headphones-hint" className="mt-4 flex items-center gap-2 text-[13px] text-emerald-200/90">
        <Headphones className="h-4 w-4" /> Headphones recommended <span className="text-emerald-300">✓</span>
        <span className="text-white/45">— so the song doesn't leak into your mic.</span>
      </p>
    </div>
  );
}

export function useSavedMic() {
  const [deviceId, setDeviceId] = useState(getSavedMic);
  return [deviceId, setDeviceId];
}
